import { useState, useCallback, useEffect } from 'react';
import { useDeals } from '@/hooks/useDeals';
import { useProducts } from '@/hooks/useProducts';
import { useThemeColor } from '@/hooks/useThemeColor';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import WhatsAppFloat from '@/pages/home/components/WhatsAppFloat';
import AnnouncementBar from '@/pages/home/components/AnnouncementBar';
import DealBanner from './components/DealBanner';
import DealProductCard from './components/DealProductCard';
import { ProductDetail } from '@/mocks/productDetails';

const DEALS_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "OfferCatalog",
  "name": "العروض والتخفيضات — متجر إبداع",
  "description": "أفضل الأسعار على منتجات الألعاب — عروض حصرية ومحدودة الوقت على PS5 وPS4 وكمبيوتر الألعاب",
  "url": `${import.meta.env.VITE_SITE_URL ?? "https://example.com"}/deals`,
  "inLanguage": "ar-SA"
};

export default function DealsPage() {
  const [barVisible, setBarVisible] = useState(true);
  const handleBarVisibility = useCallback((v: boolean) => setBarVisible(v), []);
  const { activeDeals, loading: dealsLoading } = useDeals();
  const { products, loading: productsLoading } = useProducts();
  const { primary, primaryLight: light, hexAlpha } = useThemeColor();

  const loading = dealsLoading || productsLoading;

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "schema-deals";
    script.textContent = JSON.stringify(DEALS_SCHEMA);
    document.head.appendChild(script);
    document.title = "العروض والتخفيضات — متجر إبداع AL Ebdaa";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", "أفضل الأسعار على منتجات الألعاب — عروض حصرية ومحدودة الوقت على PS5 وPS4 وكمبيوتر الألعاب في متجر إبداع.");
    return () => {
      const el = document.getElementById("schema-deals");
      if (el) el.remove();
    };
  }, []);

  const getDealProducts = (deal: ReturnType<typeof useDeals>['activeDeals'][0]): ProductDetail[] => {
    let result: ProductDetail[] = [];
    if (deal.product_ids && deal.product_ids.length > 0) {
      result = products.filter(p => deal.product_ids.includes(p.id));
    } else if (deal.badge_filter) {
      result = products.filter(p =>
        p.badge?.toLowerCase().includes(deal.badge_filter.toLowerCase())
      );
    } else {
      result = products.filter(p => p.badge && p.badge.trim() !== '');
    }
    return result;
  };

  const allDealProducts = activeDeals.length > 0
    ? getDealProducts(activeDeals[0])
    : products.filter(p => p.badge && p.badge.trim() !== '');

  return (
    <div dir="rtl" className="bg-[#0a0a0a] min-h-screen">
      <AnnouncementBar onVisibilityChange={handleBarVisibility} />
      <Navbar barVisible={barVisible} />

      <main className="pb-20">
        {/* Hero Section */}
        <div className="relative overflow-hidden pt-32 pb-20 px-4">
          <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(ellipse at top, ${hexAlpha(primary, 0.1)} 0%, transparent 60%)` }}></div>
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_rgba(239,68,68,0.06)_0%,_transparent_50%)] pointer-events-none"></div>
          <div className="absolute top-0 left-0 right-0 h-px" style={{ background: `linear-gradient(to right, transparent, ${hexAlpha(primary, 0.3)}, transparent)` }}></div>

          <div className="max-w-7xl mx-auto text-center relative z-10">
            {/* Icon mark */}
            <div className="inline-flex items-center justify-center mb-6">
              <div className="relative w-16 h-16 flex items-center justify-center">
                <div
                  className="absolute inset-0 rounded-2xl"
                  style={{
                    background: `linear-gradient(135deg, rgba(239,68,68,0.2), ${hexAlpha(primary, 0.1)})`,
                    border: `1px solid ${hexAlpha(primary, 0.3)}`,
                    boxShadow: `0 0 30px ${hexAlpha(primary, 0.15)}`,
                  }}
                ></div>
                <i className="ri-flashlight-fill text-2xl relative z-10" style={{ color: primary, textShadow: `0 0 20px ${primary}` }}></i>
              </div>
            </div>

            <p className="text-xs tracking-[0.4em] uppercase mb-4" style={{ fontFamily: "'Tajawal', sans-serif", color: hexAlpha(primary, 0.7) }}>
              عروض حصرية ومحدودة الوقت
            </p>

            <h1
              className="text-5xl md:text-7xl font-bold text-white mb-4"
              style={{ fontFamily: "'Amiri', 'Tajawal', serif", filter: `drop-shadow(0 0 40px ${hexAlpha(primary, 0.2)})` }}
            >
              العروض والتخفيضات
            </h1>

            <div className="w-24 h-0.5 mx-auto mb-6" style={{ background: `linear-gradient(to right, transparent, ${primary}, transparent)` }}></div>

            <p className="text-gray-400 text-base max-w-lg mx-auto leading-relaxed" style={{ fontFamily: "'Tajawal', sans-serif", fontWeight: 300 }}>
              أفضل الأسعار على منتجات الألعاب — لا تفوّت هذه الفرصة الذهبية
            </p>

            {/* Stats row */}
            <div className="flex items-center justify-center gap-8 mt-10 flex-wrap">
              {[
                { icon: "ri-price-tag-3-line", label: "منتج مخفّض", value: String(allDealProducts.length) },
                { icon: "ri-shield-check-line", label: "ضمان أصلي", value: "100%" },
                { icon: "ri-truck-line", label: "توصيل مجاني", value: "للجميع" },
              ].map((s) => (
                <div key={s.label} className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl" style={{ background: hexAlpha(primary, 0.1), border: `1px solid ${hexAlpha(primary, 0.2)}` }}>
                    <i className={`${s.icon} text-base`} style={{ color: primary }}></i>
                  </div>
                  <div className="text-right">
                    <p className="text-white font-bold text-base leading-none" style={{ fontFamily: "'Amiri', serif" }}>{s.value}</p>
                    <p className="text-gray-500 text-xs mt-0.5" style={{ fontFamily: "'Tajawal', sans-serif" }}>{s.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-24 gap-4">
              <div className="relative w-14 h-14">
                <div className="absolute inset-0 rounded-full border-2 border-t-2 animate-spin" style={{ borderColor: hexAlpha(primary, 0.2), borderTopColor: primary }}></div>
                <div className="absolute inset-2 rounded-full border-2 animate-spin" style={{ borderColor: hexAlpha(primary, 0.1), borderBottomColor: hexAlpha(primary, 0.4), animationDirection: "reverse", animationDuration: "0.8s" }}></div>
              </div>
              <p className="text-gray-500 text-sm" style={{ fontFamily: "'Tajawal', sans-serif" }}>جاري تحميل العروض...</p>
            </div>
          ) : (
            <>
              {/* Active Deals Banners */}
              {activeDeals.length > 0 ? (
                <div className="flex flex-col gap-6 mb-14">
                  {activeDeals.map(deal => (
                    <DealBanner key={deal.id} deal={deal} />
                  ))}
                </div>
              ) : (
                <div
                  className="relative overflow-hidden rounded-3xl p-12 text-center mb-14"
                  style={{ border: `1px solid ${hexAlpha(primary, 0.15)}`, background: `linear-gradient(135deg, ${hexAlpha(primary, 0.06)} 0%, ${hexAlpha(primary, 0.02)} 100%)` }}
                >
                  <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(ellipse at center, ${hexAlpha(primary, 0.08)} 0%, transparent 70%)` }}></div>
                  <div className="relative z-10">
                    <div
                      className="w-20 h-20 flex items-center justify-center rounded-2xl mx-auto mb-5"
                      style={{ background: hexAlpha(primary, 0.1), border: `1px solid ${hexAlpha(primary, 0.2)}`, boxShadow: `0 0 30px ${hexAlpha(primary, 0.1)}` }}
                    >
                      <i className="ri-flashlight-line text-3xl" style={{ color: primary }}></i>
                    </div>
                    <h2 className="text-3xl font-bold text-white mb-3" style={{ fontFamily: "'Amiri', 'Tajawal', serif" }}>
                      عروض قادمة قريباً
                    </h2>
                    <p className="text-gray-400 text-sm" style={{ fontFamily: "'Tajawal', sans-serif" }}>
                      تابعنا لتكون أول من يعرف بالعروض الحصرية
                    </p>
                  </div>
                </div>
              )}

              {/* Products on Sale */}
              {allDealProducts.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-8">
                    <div>
                      <p className="text-xs tracking-[0.3em] uppercase mb-1" style={{ fontFamily: "'Tajawal', sans-serif", color: hexAlpha(primary, 0.6) }}>تسوّق الآن</p>
                      <h2 className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: "'Amiri', 'Tajawal', serif" }}>
                        منتجات العرض
                      </h2>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-gray-500 text-sm" style={{ fontFamily: "'Tajawal', sans-serif" }}>
                        {allDealProducts.length} منتج
                      </span>
                      <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 px-4 py-2 rounded-full">
                        <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse flex-shrink-0"></span>
                        <span className="text-red-400 text-xs font-bold whitespace-nowrap" style={{ fontFamily: "'Tajawal', sans-serif" }}>عرض نشط</span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                    {allDealProducts.map(product => (
                      <DealProductCard
                        key={product.id}
                        product={product}
                        dealDiscount={activeDeals[0]?.discount_percent || 0}
                      />
                    ))}
                  </div>
                </div>
              )}

              {allDealProducts.length === 0 && (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <div className="w-20 h-20 flex items-center justify-center bg-[#111] border border-white/5 rounded-2xl mb-5">
                    <i className="ri-price-tag-3-line text-3xl text-gray-600"></i>
                  </div>
                  <h3 className="text-white font-bold text-lg mb-2" style={{ fontFamily: "'Amiri', 'Tajawal', serif" }}>
                    لا توجد منتجات في العرض حالياً
                  </h3>
                  <p className="text-gray-500 text-sm" style={{ fontFamily: "'Tajawal', sans-serif" }}>
                    أضف منتجات للعرض من لوحة التحكم
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
